import { create } from "zustand";
import type { InAppEventType } from "./inAppNotificationsStore";
import type { OrdreStatus } from "types/ordre";
import { notificationsApi } from "@services/api/notifications";
import type { NotifPrefValue } from "@services/api/notifications";

export type NotificationPreferences = Record<InAppEventType, NotifPrefValue>;

type NotificationPreferencesState = {
  preferences: NotificationPreferences;
  ordreStatusFilters: OrdreStatus[];
  loading: boolean;
  setPreference: (type: InAppEventType, value: NotifPrefValue) => void;
  setOrdreStatusFilter: (status: OrdreStatus, enabled: boolean) => void;
  isOrdreStatusEnabled: (statusKey: string) => boolean;
  fetchPreferences: () => Promise<void>;
};

const ALL_ORDRE_STATUSES: OrdreStatus[] = [
  "a_preparer", "devis_initial", "commande", "pret",
  "reception", "en_cours", "devis_supp", "accord", "acompte",
  "fin_de_travaux", "facturation", "solde", "solde_paye",
  "disponible", "restitue",
];

const DEFAULT_PREFERENCES: NotificationPreferences = {
  payment_received: "always",
  booking_created: "always",
  document_signed: "always",
  new_message: "always",
  devis_signed: "always",
  ordre_status_changed: "always",
  avis_received: "always",
  devis_auto_cancelled: "always",
  mention: "always",
};

/** Push current state to the server (fire-and-forget). */
function syncToServer(state: NotificationPreferencesState) {
  notificationsApi
    .updatePreferences({
      event_types: state.preferences,
      ordre_status_filters: state.ordreStatusFilters,
    })
    .catch((err) => console.warn("Failed to sync notification preferences:", err));
}

export const useNotificationPreferencesStore =
  create<NotificationPreferencesState>()(
    (set, get) => ({
      preferences: DEFAULT_PREFERENCES,
      ordreStatusFilters: [...ALL_ORDRE_STATUSES],
      loading: false,

      fetchPreferences: async () => {
        set({ loading: true });
        try {
          const data = await notificationsApi.getPreferences();
          const eventTypes = data.event_types;
          const ordreFilters = data.ordre_status_filters;
          set({
            preferences: eventTypes
              ? { ...DEFAULT_PREFERENCES, ...eventTypes } as NotificationPreferences
              : DEFAULT_PREFERENCES,
            ordreStatusFilters: (ordreFilters as OrdreStatus[]) ?? [...ALL_ORDRE_STATUSES],
          });
        } catch (err) {
          console.warn("Failed to fetch notification preferences:", err);
        } finally {
          set({ loading: false });
        }
      },

      setPreference: (type, value) => {
        set((s) => ({
          preferences: { ...s.preferences, [type]: value },
        }));
        syncToServer(get());
      },

      setOrdreStatusFilter: (status, enabled) => {
        set((s) => {
          const current = s.ordreStatusFilters;
          if (enabled && !current.includes(status)) {
            return { ordreStatusFilters: [...current, status] };
          }
          if (!enabled && current.includes(status)) {
            return { ordreStatusFilters: current.filter((st) => st !== status) };
          }
          return s;
        });
        syncToServer(get());
      },

      isOrdreStatusEnabled: (statusKey) => {
        const s = get();
        if (s.preferences.ordre_status_changed === "off") return false;
        return s.ordreStatusFilters.includes(statusKey as OrdreStatus);
      },
    }),
  );
